import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box, Typography, Button, TextField, Stack,
  ToggleButton, ToggleButtonGroup, Divider, Alert,
  MenuItem, Select, FormControl, InputLabel, Chip,
  CircularProgress
} from "@mui/material";
import { updateAvailability, updateStatus, updateRatePrice, createRatePrice } from "./inventoryService";
import axios from "axios";

// ── Étapes du drawer ─────────────────────────────────────────────
const STEPS = ["dates", "stock", "prix", "recap"];

const STEP_LABELS = {
  dates:  "Plage de dates",
  stock:  "Stock & Statut",
  prix:   "Prix",
  recap:  "Récapitulatif",
};

function StepIndicator({ current }) {
  return (
    <Stack direction="row" spacing={0} sx={{ mb: 0 }}>
      {STEPS.map((step, i) => (
        <Box key={step} sx={{ flex: 1, display: "flex", alignItems: "center" }}>
          <Box sx={{
            flex: 1,
            height: 3,
            borderRadius: 99,
            backgroundColor: i <= STEPS.indexOf(current) ? "#3A9D23" : "#E2E8F0",
            transition: "background-color 0.3s",
          }} />
        </Box>
      ))}
    </Stack>
  );
}

export default function BulkEditDrawer({ isOpen, onClose, room, onSuccess }) {
  const [step, setStep]             = useState("dates");
  const [dateDebut, setDateDebut]   = useState("");
  const [dateFin, setDateFin]       = useState("");
  const [stock, setStock]           = useState("");
  const [statut, setStatut]         = useState(null);
  const [modifyPrice, setModifyPrice] = useState(false);
  const [ratePlans, setRatePlans]   = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [newPrice, setNewPrice]     = useState("");
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [success, setSuccess]       = useState(false);

  // Fetch rate plans de la chambre
  useEffect(() => {
    if (!isOpen || !room?.roomId) return;
    const fetchPlans = async () => {
      try {
        setLoadingPlans(true);
        const res = await axios.get(
          `https://api.komorotel.com/api/rateprices/room/${room.roomId}`,
          { withCredentials: true }
        );
        // Déduplique par ratePlanId
        const unique = [];
        const seen = new Set();
        for (const rp of res.data) {
        const planId = rp.ratePlanId?._id || rp.ratePlanId;
        if (!seen.has(planId)) {
            seen.add(planId);
            unique.push({
            ratePlanId: planId,
            ratePlanName: rp.ratePlanId?.name || "—", // ✅ depuis le populate
            });
        }
        }
        setRatePlans(unique);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, [isOpen, room?.roomId]);

  const reset = () => {
    setStep("dates");
    setDateDebut(""); setDateFin("");
    setStock(""); setStatut(null);
    setModifyPrice(false);
    setSelectedPlan(""); setNewPrice("");
    setError(null); setSuccess(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const addOneDay = (dateStr) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString("sv-SE");
  };

  // ── Validation par étape ────────────────────────────────────────
  const canNext = () => {
    if (step === "dates") return dateDebut && dateFin && new Date(dateFin) >= new Date(dateDebut);
    if (step === "stock") return true; // optionnel
    if (step === "prix")  return !modifyPrice || (selectedPlan && newPrice !== "");
    return true;
  };

  const nextStep = () => {
    const idx = STEPS.indexOf(step);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1]);
  };

  const prevStep = () => {
    const idx = STEPS.indexOf(step);
    if (idx > 0) setStep(STEPS[idx - 1]);
  };

  // ── Apply ────────────────────────────────────────────────────────
  const handleApply = async () => {
    setError(null);
    try {
      setLoading(true);
      const fin = addOneDay(dateFin);

      // Stock
      if (stock !== "") {
        await updateAvailability(room.roomId, dateDebut, fin, parseInt(stock));
      }

      // Statut
      if (statut !== null) {
        await updateStatus(room.roomId, dateDebut, fin, statut);
      }

      
      // Prix — toujours createRatePrice sur une plage
        if (modifyPrice && selectedPlan && newPrice !== "") {
        await createRatePrice(
            room.roomId,
            selectedPlan,
            parseInt(newPrice),
            dateDebut,
            fin
        );
        }

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        handleClose();
      }, 1200);

    } catch (err) {
      setError("Une erreur est survenue. Vérifiez les données.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectedPlanLabel = ratePlans.find(r => r.ratePlanId === selectedPlan)?.ratePlanName || selectedPlan;

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        style={{
          position: "fixed", inset: 0,
          background: "rgba(15,23,42,0.45)",
          backdropFilter: "blur(2px)",
          zIndex: 1300,
          display: "flex", justifyContent: "flex-end",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          style={{
            width: 460, height: "100%",
            background: "#fff",
            boxShadow: "-8px 0 32px rgba(0,0,0,0.12)",
            display: "flex", flexDirection: "column",
          }}
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", damping: 28, stiffness: 280 }}
          onClick={(e) => e.stopPropagation()}
        >

          {/* ── Header ──────────────────────────────────────────── */}
          <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: "1px solid #F1F5F9" }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={2}>
              <Box>
                <Typography fontWeight={700} fontSize={17} color="#0F172A">
                  Modification en masse
                </Typography>
                <Typography fontSize={13} color="#94A3B8" mt={0.3}>
                  {room?.title || "Chambre"}
                </Typography>
              </Box>
              <button
                onClick={handleClose}
                style={{
                  background: "#F8FAFC", border: "1px solid #E2E8F0",
                  borderRadius: 8, width: 32, height: 32,
                  cursor: "pointer", color: "#64748B", fontSize: 15,
                  display: "flex", alignItems: "center", justifyContent: "center"
                }}
              >
                ✕
              </button>
            </Stack>

            {/* Step indicator */}
            <StepIndicator current={step} />
            <Stack direction="row" justifyContent="space-between" mt={1}>
              {STEPS.map((s) => (
                <Typography key={s} fontSize={10} color={s === step ? "#3A9D23" : "#CBD5E1"} fontWeight={s === step ? 600 : 400}>
                  {STEP_LABELS[s]}
                </Typography>
              ))}
            </Stack>
          </Box>

          {/* ── Body ────────────────────────────────────────────── */}
          <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 3 }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.18 }}
              >

                {/* ── ÉTAPE 1 : DATES ───────────────────────────── */}
                {step === "dates" && (
                  <Stack spacing={3}>
                    <Box>
                      <Typography fontWeight={600} fontSize={14} color="#0F172A" mb={0.5}>
                        Sélectionnez la plage de dates
                      </Typography>
                      <Typography fontSize={13} color="#94A3B8" mb={2}>
                        Les modifications s'appliqueront à toutes les nuits comprises dans cette plage.
                      </Typography>
                      <Stack spacing={2}>
                        <TextField
                          label="Date de début"
                          type="date"
                          value={dateDebut}
                          onChange={(e) => setDateDebut(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          inputProps={{ min: new Date().toLocaleDateString("sv-SE") }}
                        />
                        <TextField
                          label="Date de fin"
                          type="date"
                          value={dateFin}
                          onChange={(e) => setDateFin(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          size="small"
                          inputProps={{ min: dateDebut || new Date().toLocaleDateString("sv-SE") }}
                        />
                      </Stack>
                      {dateDebut && dateFin && new Date(dateFin) >= new Date(dateDebut) && (
                        <Box sx={{ mt: 2, p: 2, background: "#F0FDF4", borderRadius: 2, border: "1px solid #BBF7D0" }}>
                          <Typography fontSize={13} color="#15803D" fontWeight={500}>
                            📅 {Math.round((new Date(dateFin) - new Date(dateDebut)) / 86400000) + 1} nuit(s) sélectionnée(s)
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Stack>
                )}

                {/* ── ÉTAPE 2 : STOCK & STATUT ──────────────────── */}
                {step === "stock" && (
                  <Stack spacing={3}>
                    {/* Stock */}
                    <Box>
                      <Typography fontWeight={600} fontSize={14} color="#0F172A" mb={0.5}>
                        Stock disponible
                      </Typography>
                      <Typography fontSize={13} color="#94A3B8" mb={2}>
                        Nombre de chambres disponibles par nuit. Laisser vide pour ne pas modifier.
                      </Typography>
                      <TextField
                        type="number"
                        placeholder="Ex : 2"
                        value={stock}
                        onChange={(e) => setStock(e.target.value)}
                        size="small"
                        fullWidth
                        inputProps={{ min: 0 }}
                        InputProps={{
                          endAdornment: <Typography fontSize={12} color="#94A3B8">chambre(s)</Typography>
                        }}
                      />
                    </Box>

                    <Divider />

                    {/* Statut */}
                    <Box>
                      <Typography fontWeight={600} fontSize={14} color="#0F172A" mb={0.5}>
                        Statut
                      </Typography>
                      <Typography fontSize={13} color="#94A3B8" mb={2}>
                        Ouvrir ou fermer la chambre sur cette période. Laisser sans sélection pour ne pas modifier.
                      </Typography>
                      <ToggleButtonGroup
                        value={statut}
                        exclusive
                        onChange={(_, val) => setStatut(val)}
                        size="small"
                        fullWidth
                      >
                        <ToggleButton
                          value="opened"
                          sx={{
                            py: 1.5, fontSize: 13, fontWeight: 500,
                            "&.Mui-selected": {
                              backgroundColor: "#F0FDF4",
                              color: "#15803D",
                              borderColor: "#86EFAC",
                            }
                          }}
                        >
                          ✓ Ouvert
                        </ToggleButton>
                        <ToggleButton
                          value="closed"
                          sx={{
                            py: 1.5, fontSize: 13, fontWeight: 500,
                            "&.Mui-selected": {
                              backgroundColor: "#FEF2F2",
                              color: "#B91C1C",
                              borderColor: "#FCA5A5",
                            }
                          }}
                        >
                          ✕ Fermé
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </Stack>
                )}

                {/* ── ÉTAPE 3 : PRIX ────────────────────────────── */}
                {step === "prix" && (
                  <Stack spacing={3}>
                    <Box>
                      <Typography fontWeight={600} fontSize={14} color="#0F172A" mb={0.5}>
                        Modifier le prix d'un tarif
                      </Typography>
                      <Typography fontSize={13} color="#94A3B8" mb={2}>
                        Optionnel — si un prix existe déjà sur cette plage, il sera mis à jour. Sinon un nouveau prix sera créé.
                      </Typography>

                      {/* Toggle modifier prix */}
                      <Stack direction="row" alignItems="center" justifyContent="space-between"
                        sx={{ p: 2, border: "1px solid #E2E8F0", borderRadius: 2, cursor: "pointer", mb: 2 }}
                        onClick={() => setModifyPrice(!modifyPrice)}
                      >
                        <Typography fontSize={13} fontWeight={500} color="#374151">
                          Modifier le prix sur cette plage
                        </Typography>
                        <Box sx={{
                          width: 40, height: 22, borderRadius: 99,
                          backgroundColor: modifyPrice ? "#3A9D23" : "#E2E8F0",
                          position: "relative", transition: "background 0.2s",
                        }}>
                          <Box sx={{
                            position: "absolute",
                            top: 3, left: modifyPrice ? 21 : 3,
                            width: 16, height: 16,
                            borderRadius: "50%", background: "#fff",
                            transition: "left 0.2s",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                          }} />
                        </Box>
                      </Stack>

                      {modifyPrice && (
                        <Stack spacing={2}>
                          {/* Sélection du tarif */}
                          <FormControl size="small" fullWidth>
                            <InputLabel>Tarif</InputLabel>
                            <Select
                              value={selectedPlan}
                              onChange={(e) => setSelectedPlan(e.target.value)}
                              label="Tarif"
                            >
                              {loadingPlans ? (
                                <MenuItem disabled>
                                  <CircularProgress size={14} sx={{ mr: 1 }} /> Chargement...
                                </MenuItem>
                              ) : ratePlans.length === 0 ? (
                                <MenuItem disabled>Aucun tarif configuré</MenuItem>
                              ) : (
                                ratePlans.map((rp) => (
                                  <MenuItem key={rp.ratePlanId} value={rp.ratePlanId}>
                                    {rp.ratePlanName || rp.ratePlanId}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>

                          {/* Nouveau prix */}
                          <TextField
                            type="number"
                            label="Nouveau prix"
                            placeholder="Ex : 120"
                            value={newPrice}
                            onChange={(e) => setNewPrice(e.target.value)}
                            size="small"
                            fullWidth
                            inputProps={{ min: 0 }}
                            InputProps={{
                              endAdornment: <Typography fontSize={12} color="#94A3B8">€ / nuit</Typography>
                            }}
                          />
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                )}

                {/* ── ÉTAPE 4 : RECAP ───────────────────────────── */}
                {step === "recap" && (
                  <Stack spacing={2}>
                    <Typography fontWeight={600} fontSize={14} color="#0F172A" mb={1}>
                      Récapitulatif des modifications
                    </Typography>

                    {/* Card recap */}
                    <Box sx={{ border: "1px solid #E2E8F0", borderRadius: 2, overflow: "hidden" }}>
                      {[
                        {
                          label: "Chambre",
                          value: room?.title,
                          icon: "🏨"
                        },
                        {
                          label: "Plage",
                          value: `${new Date(dateDebut).toLocaleDateString("fr-FR")} → ${new Date(dateFin).toLocaleDateString("fr-FR")}`,
                          icon: "📅"
                        },
                        stock !== "" && {
                          label: "Stock",
                          value: `${stock} chambre(s) / nuit`,
                          icon: "📦"
                        },
                        statut && {
                          label: "Statut",
                          value: statut === "opened" ? "Ouvert" : "Fermé",
                          icon: statut === "opened" ? "✅" : "🔴",
                          color: statut === "opened" ? "#15803D" : "#B91C1C"
                        },
                        modifyPrice && selectedPlan && newPrice && {
                          label: "Prix",
                          value: `${newPrice} € / nuit — ${selectedPlanLabel}`,
                          icon: "💶"
                        },
                      ].filter(Boolean).map((item, i, arr) => (
                        <Box key={i} sx={{
                          px: 3, py: 2,
                          borderBottom: i < arr.length - 1 ? "1px solid #F1F5F9" : "none",
                          display: "flex", justifyContent: "space-between", alignItems: "center"
                        }}>
                          <Typography fontSize={13} color="#64748B">
                            {item.icon} {item.label}
                          </Typography>
                          <Typography fontSize={13} fontWeight={600} color={item.color || "#0F172A"}>
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Avertissement si rien à modifier */}
                    {stock === "" && statut === null && !modifyPrice && (
                      <Alert severity="warning" sx={{ fontSize: 13 }}>
                        Aucune modification sélectionnée. Revenez en arrière pour modifier stock, statut ou prix.
                      </Alert>
                    )}

                    {error   && <Alert severity="error">{error}</Alert>}
                    {success && <Alert severity="success">✓ Modifications appliquées avec succès</Alert>}
                  </Stack>
                )}

              </motion.div>
            </AnimatePresence>
          </Box>

          {/* ── Footer navigation ────────────────────────────────── */}
          <Box sx={{ px: 3, py: 2.5, borderTop: "1px solid #F1F5F9" }}>
            <Stack direction="row" spacing={2}>
              {step !== "dates" && (
                <Button
                  variant="outlined"
                  onClick={prevStep}
                  disabled={loading}
                  sx={{ borderColor: "#E2E8F0", color: "#64748B", minWidth: 80 }}
                >
                  ← Retour
                </Button>
              )}

              {step !== "recap" ? (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={nextStep}
                  disabled={!canNext()}
                  sx={{ backgroundColor: "#3A9D23", "&:hover": { backgroundColor: "#2d7b1c" }, py: 1.5 }}
                >
                  Continuer →
                </Button>
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleApply}
                  disabled={loading || success || (stock === "" && statut === null && !modifyPrice)}
                  sx={{ backgroundColor: "#3A9D23", "&:hover": { backgroundColor: "#2d7b1c" }, py: 1.5 }}
                >
                  {loading
                    ? <><CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} /> Application...</>
                    : "Appliquer les modifications"
                  }
                </Button>
              )}
            </Stack>
          </Box>

        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}